import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, money, fmtDate, today, addDays, toPaise, ApiError, DOC_LABELS } from '../api'
import { Page } from '../App'
import { Card, Badge, Alert, Spinner, Modal, Field, CopyButton, Tabs, Confirm } from '../components/ui'
import SendEmail from '../components/SendEmail'
import SendWhatsApp from '../components/SendWhatsApp'

interface EmailKit {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  text: string
  html: string
  mailtoUrl: string
  emlBase64: string
  emlFilename: string
  hasAttachment: boolean
}

interface FullInvoice {
  invoice: Record<string, string | number | null>
  lines: Record<string, string | number | null>[]
  client: Record<string, string | number | null>
  payments: Record<string, string | number | null>[]
  editable: boolean
  docLabel: string
  treatmentLabel: string
  publicUrl: string | null
  pdfAvailable: boolean
  emails: Record<string, string | number | null>[]
  reminders: Record<string, string | number | null>[]
}

export default function InvoiceView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<FullInvoice | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [showKit, setShowKit] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [showWhatsApp, setShowWhatsApp] = useState(false)
  const [showPay, setShowPay] = useState(false)
  const [confirmAction, setConfirmAction] = useState<'cancel' | 'writeoff' | 'reopen' | null>(null)

  const load = async () => {
    try {
      setData(await api.get<FullInvoice>(`/invoices/${id}`))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load invoice')
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (error) return <Page title="Invoice"><Alert kind="error">{error}</Alert></Page>
  if (!data) return <Page title="Invoice"><Spinner /></Page>

  const inv = data.invoice
  const cur = String(inv.currency)
  const status = String(inv.status)
  const isDraft = status === 'draft'
  const isOpen = ['issued', 'sent', 'viewed', 'partially_paid', 'overdue'].includes(status)

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label)
    setError('')
    setNotice('')
    try {
      await fn()
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Action failed')
    } finally {
      setBusy('')
    }
  }

  return (
    <Page
      title={
        <span>
          {data.docLabel} <span className="mono">{inv.number ?? '(draft)'}</span>
        </span>
      }
      subtitle={
        <>
          {String(inv.client_display_name ?? data.client.display_name ?? data.client.legal_name)} ·{' '}
          {fmtDate(String(inv.issue_date))} · {data.treatmentLabel}
        </>
      }
      actions={
        <>
          <Badge status={status} />
          {isDraft && (
            <>
              <button className="btn" onClick={() => navigate(`/invoices/${id}/edit`)}>
                Edit
              </button>
              <button
                className="btn primary"
                disabled={busy !== ''}
                onClick={() => act('finalize', () => api.post(`/invoices/${id}/finalize`))}
              >
                {busy === 'finalize' ? 'Finalizing…' : 'Finalize'}
              </button>
            </>
          )}
          {!isDraft && (
            <>
              <a className="btn" href={`/api/invoices/${id}/html`} target="_blank" rel="noreferrer">
                Print view
              </a>
              {data.pdfAvailable && (
                <a className="btn" href={`/api/invoices/${id}/pdf`}>
                  Download PDF
                </a>
              )}
              <button className="btn" onClick={() => setShowKit(true)}>
                Email kit
              </button>
              <button className="btn" onClick={() => setShowWhatsApp(true)}>
                WhatsApp
              </button>
              <button className="btn primary" onClick={() => setShowSend(true)}>
                ✉ Send email
              </button>
            </>
          )}
        </>
      }
    >
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>

      {isDraft && (
        <Alert kind="info">
          This is a draft. It has no invoice number and cannot be sent or paid. Finalizing assigns
          the next number in the series and locks the invoice — after that, corrections go through a
          credit note.
        </Alert>
      )}

      <div className="grid c4" style={{ marginBottom: 14 }}>
        <div className="stat">
          <div className="k">Total</div>
          <div className="v">{money(Number(inv.total), cur)}</div>
        </div>
        <div className="stat">
          <div className="k">Paid</div>
          <div className="v">{money(Number(inv.amount_paid), cur)}</div>
          {Number(inv.tds_total) > 0 && <div className="n">+ {money(Number(inv.tds_total), cur)} TDS</div>}
        </div>
        <div className={`stat ${Number(inv.balance) > 0 ? 'danger' : 'ok'}`}>
          <div className="k">Balance</div>
          <div className="v">{money(Math.max(0, Number(inv.balance)), cur)}</div>
        </div>
        <div className="stat">
          <div className="k">Due date</div>
          <div className="v" style={{ fontSize: 17 }}>
            {fmtDate(String(inv.due_date))}
          </div>
          {status === 'overdue' && (
            <div className="n" style={{ color: 'var(--danger)' }}>
              Past due
            </div>
          )}
        </div>
      </div>

      <div className="grid c2" style={{ marginBottom: 14 }}>
        <Card title="Line items" tight>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>HSN/SAC</th>
                  <th className="r">Qty</th>
                  <th className="r">Rate</th>
                  <th className="r">Taxable</th>
                  <th className="r">Tax</th>
                  <th className="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{String(l.description)}</td>
                    <td className="mono small">{String(l.sac_hsn ?? '—')}</td>
                    <td className="num">
                      {Number(l.qty)} {String(l.unit)}
                    </td>
                    <td className="num">{money(Number(l.rate), cur)}</td>
                    <td className="num">{money(Number(l.taxable_value), cur)}</td>
                    <td className="num">
                      {money(Number(l.cgst_amt) + Number(l.sgst_amt) + Number(l.igst_amt), cur)}
                    </td>
                    <td className="num">
                      <strong>{money(Number(l.line_total), cur)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div>
          <Card
            title="Payments"
            actions={
              isOpen ? (
                <button className="btn sm primary" onClick={() => setShowPay(true)}>
                  + Record payment
                </button>
              ) : null
            }
            tight
          >
            {data.payments.length === 0 ? (
              <div className="empty small">No payments recorded yet.</div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th className="r">TDS</th>
                    <th className="r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p) => (
                    <tr key={String(p.id)}>
                      <td className="nowrap">{fmtDate(String(p.paid_on))}</td>
                      <td className="small">{String(p.method).toUpperCase()}</td>
                      <td className="mono small">{String(p.reference_utr ?? '—')}</td>
                      <td className="num">{Number(p.tds_amount) ? money(Number(p.tds_amount), cur) : '—'}</td>
                      <td className="num">
                        <strong>{money(Number(p.amount), cur)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <div style={{ height: 14 }} />

          <Card title="Delivery & reminders" tight>
            <table className="tbl">
              <tbody>
                <tr>
                  <td>Sent</td>
                  <td className="right">{inv.sent_at ? fmtDate(String(inv.sent_at).slice(0, 10)) : <span className="muted">Not yet</span>}</td>
                </tr>
                <tr>
                  <td>Opened by client</td>
                  <td className="right">
                    {inv.viewed_at ? (
                      <Badge status="viewed">{fmtDate(String(inv.viewed_at).slice(0, 10))}</Badge>
                    ) : (
                      <span className="muted">Not yet</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Reminders sent</td>
                  <td className="right">{data.reminders.length}</td>
                </tr>
                <tr>
                  <td>Emails logged</td>
                  <td className="right">{data.emails.length}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {data.publicUrl && !isDraft && (
        <Card title="Shareable link" >
          <div className="copyable">
            <pre>{data.publicUrl}</pre>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <CopyButton text={data.publicUrl} label="Copy link" />
              <a className="btn sm" href={data.publicUrl} target="_blank" rel="noreferrer">
                Open
              </a>
            </div>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Anyone with this link can view and print the invoice. Opening it marks the invoice as
            viewed, so you can tell whether the client actually saw it.
          </div>
        </Card>
      )}

      {!isDraft && (
        <>
          <div style={{ height: 14 }} />
          <Card title="Other actions">
            <div className="btn-row">
              <button className="btn" disabled={busy !== ''} onClick={() => act('dup', async () => {
                const r = await api.post<{ id: string }>(`/invoices/${id}/duplicate`)
                navigate(`/invoices/${r.id}/edit`)
              })}>
                Duplicate as draft
              </button>
              {['proforma', 'estimate'].includes(String(inv.doc_type)) && (
                <button className="btn primary" disabled={busy !== ''} onClick={() => act('conv', async () => {
                  const r = await api.post<{ id: string }>(`/invoices/${id}/convert`)
                  navigate(`/invoices/${r.id}/edit`)
                })}>
                  Convert to tax invoice
                </button>
              )}
              {String(inv.doc_type) === 'tax_invoice' && (
                <button className="btn" disabled={busy !== ''} onClick={() => act('cn', async () => {
                  const r = await api.post<{ id: string }>(`/invoices/${id}/credit-note`)
                  navigate(`/invoices/${r.id}/edit`)
                })}>
                  Raise credit note
                </button>
              )}
              <button className="btn" disabled={busy !== ''} onClick={() => act('marksent', () => api.post(`/invoices/${id}/mark-sent`))}>
                Mark as sent manually
              </button>
              {!inv.sent_at && !inv.viewed_at && Number(inv.amount_paid) === 0 && status !== 'cancelled' && (
                <button className="btn" onClick={() => setConfirmAction('reopen')}>
                  Reopen for editing
                </button>
              )}
              {isOpen && (
                <>
                  <button className="btn" onClick={() => act('snooze', () =>
                    api.post(`/invoices/${id}/snooze`, { until: addDays(today(), 7), reason: 'Snoozed from invoice view' }),
                  )}>
                    Snooze reminders 7 days
                  </button>
                  <button className="btn danger" onClick={() => setConfirmAction('writeoff')}>
                    Write off
                  </button>
                </>
              )}
              {status !== 'cancelled' && Number(inv.amount_paid) === 0 && (
                <button className="btn danger" onClick={() => setConfirmAction('cancel')}>
                  Cancel invoice
                </button>
              )}
            </div>
          </Card>
        </>
      )}

      {showKit && <EmailKitModal invoiceId={String(id)} onClose={() => setShowKit(false)} />}

      {showWhatsApp && (
        <SendWhatsApp
          invoiceId={String(id)}
          invoiceNumber={String(inv.number ?? '')}
          onClose={() => setShowWhatsApp(false)}
          onSent={(msg) => setNotice(msg)}
        />
      )}

      {showSend && (
        <SendEmail
          invoiceId={String(id)}
          invoiceNumber={String(inv.number ?? '')}
          onClose={() => setShowSend(false)}
          onSent={(msg) => {
            setNotice(msg)
            load()
          }}
        />
      )}

      {showPay && (
        <PaymentModal
          invoiceId={String(id)}
          currency={cur}
          balance={Number(inv.balance)}
          onClose={() => setShowPay(false)}
          onSaved={() => {
            setShowPay(false)
            load()
          }}
        />
      )}

      {confirmAction && (
        <Confirm
          title={
            confirmAction === 'cancel'
              ? 'Cancel this invoice?'
              : confirmAction === 'reopen'
                ? 'Reopen this invoice for editing?'
                : 'Write off this invoice?'
          }
          danger={confirmAction !== 'reopen'}
          requireReason
          confirmLabel={
            confirmAction === 'cancel'
              ? 'Cancel invoice'
              : confirmAction === 'reopen'
                ? 'Reopen'
                : 'Write off'
          }
          message={
            confirmAction === 'cancel'
              ? 'The invoice number is retained and never reused — a cancelled number is fine, a reused one is an audit finding. This cannot be undone.'
              : confirmAction === 'reopen'
                ? `It goes back to draft so you can correct it, and keeps the number ${inv.number ?? ''} — re-finalizing reissues the same one rather than burning the next. Only possible because it has not been sent, opened or paid. The reason is recorded in the audit log.`
                : 'The balance will be treated as unrecoverable and reminders will stop. The invoice stays on record.'
          }
          onCancel={() => setConfirmAction(null)}
          onConfirm={async (reason) => {
            const path =
              confirmAction === 'cancel' ? 'cancel' : confirmAction === 'reopen' ? 'reopen' : 'write-off'
            const wasReopen = confirmAction === 'reopen'
            setConfirmAction(null)
            await act('cancel', async () => {
              await api.post(`/invoices/${id}/${path}`, { reason })
              if (wasReopen) navigate(`/invoices/${id}/edit`)
            })
          }}
        />
      )}
    </Page>
  )
}

// ---------------------------------------------------------------- email kit

function EmailKitModal({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const [kit, setKit] = useState<EmailKit | null>(null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('text')

  useEffect(() => {
    api
      .get<{ kit: EmailKit }>(`/invoices/${invoiceId}/email-kit`)
      .then((r) => setKit(r.kit))
      .catch((e) => setError(e.message))
  }, [invoiceId])

  const downloadEml = () => {
    if (!kit) return
    const bin = atob(kit.emlBase64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const url = URL.createObjectURL(new Blob([bytes], { type: 'message/rfc822' }))
    const a = document.createElement('a')
    a.href = url
    a.download = kit.emlFilename
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal wide title="Email kit" onClose={onClose}>
      <Alert kind="error">{error}</Alert>
      {!kit ? (
        <Spinner />
      ) : (
        <>
          <p className="muted small" style={{ marginTop: 0 }}>
            Everything you need to send this yourself from Gmail. Nothing here has been transmitted.
          </p>

          <div className="grid c2">
            <Field label="To">
              <div className="copyable">
                <pre>{kit.to.join(', ') || '(no recipient)'}</pre>
                <CopyButton text={kit.to.join(', ')} />
              </div>
            </Field>
            <Field label="CC / BCC">
              <div className="copyable">
                <pre>{[...kit.cc, ...kit.bcc].join(', ') || '—'}</pre>
                <CopyButton text={[...kit.cc, ...kit.bcc].join(', ')} />
              </div>
            </Field>
          </div>

          <Field label="Subject">
            <div className="copyable">
              <pre>{kit.subject}</pre>
              <CopyButton text={kit.subject} />
            </div>
          </Field>

          <Tabs
            tabs={[
              { key: 'text', label: 'Plain text body' },
              { key: 'html', label: 'HTML body' },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === 'text' ? (
            <div className="copyable">
              <pre>{kit.text}</pre>
              <CopyButton text={kit.text} label="Copy body" />
            </div>
          ) : (
            <div className="copyable">
              <pre>{kit.html}</pre>
              <CopyButton text={kit.html} label="Copy HTML" />
            </div>
          )}

          <div className="sep" />

          <div className="btn-row">
            <a className="btn primary" href={kit.mailtoUrl}>
              Open in mail app
            </a>
            <button className="btn" onClick={downloadEml}>
              Download .eml {kit.hasAttachment ? '(with PDF attached)' : ''}
            </button>
            <a className="btn" href={`/api/invoices/${invoiceId}/pdf`}>
              Download PDF
            </a>
          </div>
          {!kit.hasAttachment && (
            <div className="hint" style={{ marginTop: 8 }}>
              No PDF is attached because Browser Rendering is not configured yet. The email still
              carries the public invoice link. Set CF_ACCOUNT_ID and CF_API_TOKEN to enable PDFs.
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

// ------------------------------------------------------------ payment modal

export function PaymentModal({
  invoiceId,
  currency,
  balance,
  onClose,
  onSaved,
}: {
  invoiceId: string
  currency: string
  balance: number
  onClose: () => void
  onSaved: () => void
}) {
  const [amount, setAmount] = useState((balance / 100).toFixed(2))
  const [tds, setTds] = useState('0')
  const [paidOn, setPaidOn] = useState(today())
  const [method, setMethod] = useState('upi')
  const [ref, setRef] = useState('')
  const [notes, setNotes] = useState('')
  const [sendReceipt, setSendReceipt] = useState(false)
  const [error, setError] = useState('')
  const [confirmOver, setConfirmOver] = useState(false)
  const [busy, setBusy] = useState(false)

  const save = async (allowOverpayment = false) => {
    setError('')
    setBusy(true)
    try {
      await api.post('/payments', {
        invoice_id: invoiceId,
        amount_paise: toPaise(amount),
        tds_paise: toPaise(tds),
        paid_on: paidOn,
        method,
        reference_utr: ref || null,
        notes: notes || null,
        send_receipt: sendReceipt,
        allow_overpayment: allowOverpayment,
      })
      onSaved()
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConfirmOver(true)
        setError(e.message)
      } else setError(e instanceof ApiError ? e.message : 'Could not record the payment')
    } finally {
      setBusy(false)
    }
  }

  const settles = toPaise(amount) + toPaise(tds)

  return (
    <Modal
      title="Record a payment"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy} onClick={() => save(confirmOver)}>
            {busy ? 'Saving…' : confirmOver ? 'Record anyway' : 'Record payment'}
          </button>
        </>
      }
    >
      <Alert kind={confirmOver ? 'warn' : 'error'}>{error}</Alert>

      <div className="grid c2">
        <Field label={`Amount received (${currency})`}>
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        <Field
          label="TDS deducted by client"
          hint="Enter what they withheld, e.g. 10% under 194J. The invoice then settles at zero."
        >
          <input type="number" step="0.01" value={tds} onChange={(e) => setTds(e.target.value)} />
        </Field>
      </div>

      <div className="alert info small" style={{ marginTop: -4 }}>
        Settling {money(settles, currency)} of {money(balance, currency)} outstanding.
        {settles < balance && ` ${money(balance - settles, currency)} will remain due.`}
      </div>

      <div className="grid c2">
        <Field label="Payment date">
          <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label="Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            {['upi', 'neft', 'imps', 'rtgs', 'cheque', 'cash', 'card', 'other'].map((m) => (
              <option key={m} value={m}>
                {m.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Reference / UTR" hint="The number the client sends after paying.">
        <input type="text" className="mono" value={ref} onChange={(e) => setRef(e.target.value)} />
      </Field>
      <Field label="Notes">
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      <label className="check">
        <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />
        Email a receipt to the client
      </label>
    </Modal>
  )
}
